/**
 * Class strings copied from the kit's TopHeader panel primitives
 * (design-system/11-navigation/TopHeader.md § "Panel menus") so every header
 * dropdown renders identical chrome without importing kit components.
 *
 * Kit sources: `Popover` `baseContent` + `TopHeaderMenu` (panel) and
 * `TopHeaderMenuItem` (rows). Radix drives the kit's hover state via
 * `data-[highlighted]`; with plain markup the equivalent is `hover:` +
 * `focus-visible:`.
 *
 * Shared by the Tasks panel in `components/common/Header.tsx` and
 * `HeaderNewRequestMenu` — keep the styling here, not duplicated per panel.
 */

/** Panel shell — positioning + kit Popover `baseContent`, width 300. */
export const MENU_PANEL_CLASSES =
  'tw-absolute tw-top-full tw-end-0 tw-mt-[8px] tw-z-[80] tw-flex tw-flex-col ' +
  'tw-w-[300px] tw-max-w-[calc(100vw_-_16px)] tw-bg-white tw-border-[0.5px] tw-border-gray-200 ' +
  'tw-rounded-[8px] tw-shadow-[0_4px_12px_rgba(0,0,0,0.08),0_2px_4px_rgba(0,0,0,0.04)] ' +
  'tw-overflow-hidden tw-outline-none';

/** Panel title row — kit TopHeaderMenu `title` / `titleEnd` header. */
export const MENU_PANEL_HEADER_CLASSES =
  'tw-flex tw-items-center tw-justify-between tw-gap-[8px] tw-px-[16px] tw-py-[10px] ' +
  'tw-border-b-[0.5px] tw-border-gray-100';

export const MENU_PANEL_TITLE_CLASSES =
  'tw-text-[13px] tw-font-medium tw-text-gray-800 tw-leading-tight';

/** Scroll area — kit TopHeaderMenu body (p-[4px], maxHeight 360). */
export const MENU_PANEL_BODY_CLASSES =
  'tw-overflow-y-auto tw-p-[4px] tw-max-h-[360px]';

/** Sticky footer — kit TopHeaderMenu `footer`, holds one menu row. */
export const MENU_PANEL_FOOTER_CLASSES =
  'tw-px-[8px] tw-py-[8px] tw-border-t-[0.5px] tw-border-gray-100';

/** Row — kit `TopHeaderMenuItem`. */
export const MENU_ROW_CLASSES =
  'tw-group tw-w-full tw-flex tw-items-center tw-gap-[10px] tw-text-start ' +
  'tw-ps-[10px] tw-pe-[10px] tw-py-[8px] tw-rounded-[6px] ' +
  'tw-bg-transparent tw-border-0 tw-cursor-pointer tw-transition-colors tw-duration-100 ' +
  'hover:tw-bg-gray-50 focus-visible:tw-outline-none focus-visible:tw-bg-gray-50';

/** Row label — single-line, kit `TopHeaderMenuItem` label. */
export const MENU_ROW_LABEL_CLASSES =
  'tw-block tw-text-[13px] tw-font-medium tw-truncate tw-text-gray-700 group-hover:tw-text-gray-800';

/** Row secondary line — kit `TopHeaderMenuItem` `description`. */
export const MENU_ROW_DESCRIPTION_CLASSES =
  'tw-block tw-text-[11px] tw-text-gray-500 tw-truncate tw-mt-[2px]';

/** Row icon slot (start side) — kit `TopHeaderMenuItem` icon. */
export const MENU_ROW_ICON_CLASSES =
  'tw-inline-flex tw-items-center tw-justify-center tw-flex-none tw-w-[16px] ' +
  'tw-text-gray-400 group-hover:tw-text-gray-600';

/** Row trailing slot (end side) — kit `TopHeaderMenuItem` `trailing`. */
export const MENU_ROW_TRAILING_CLASSES = 'tw-flex-none tw-text-gray-400';
